import { Injectable } from '@angular/core';
import { ApiService, GenericResponsePage } from '@uranda/commons';
import { Lead, LeadSource, LeadStatus, VerificationFilter } from '../../../../models/lead.model';
import { HttpParams } from '@angular/common/http';
import { ContactRequest, ContactStatus } from '../../../../models/contact-request.model';
import { Partner, PartnerStatus } from '../../../../models/partner.model';
import { QuoteRequest, OfferRequestStatusEnum } from '../../../../models/offer.request.model';
import { OfferRequestFilter } from '../../../../models/requests/offerRequestFilter.model';
import { ContactRequestFilter } from '../../../../models/requests/contactRequestFilter';
import { Quote } from '../../../../models/quote.model';
import { DashboardCounters } from '../../../../models/dashboardCounters.model';
import {
  QuoteRequestStats,
  QuoteRequestTimeSeries,
  ContactRequestTimeSeries,
  RevenueTimeSeries,
  PartnerQuotePerformance,
  PartnerContactPerformance
} from '../../../../models/admin-dashboard.model';
import { QrCode } from '../../../../models/qr-codes.model';
import { TopPartner } from '../../../../models/topPartner.model';
import { PartnerFilter } from '../../../../models/partner-quote-request.model';
import { ColumnSorting, SortingUtilsService } from '@uranda/ui';
import { Report } from '../../../../models/reports.model';
import { SmsMessage } from '../../../../models/sms.model';
import { Invoice, InvoiceFilters, InvoicePaymentMethod } from '../../../../models/invoices.model';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AdminService extends ApiService {
  sortingUtils = this.injector.get(SortingUtilsService);

  async getLeads(
    page = 0,
    size = 10,
    filters: {
      status?: LeadStatus;
      verificationStatus?: VerificationFilter;
      leadSource?: LeadSource;
    } = {},
    q?: string,
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<Lead[]>> {
    let queryParameters = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('q', q ?? '');

    if (filters.status) {
      queryParameters = queryParameters.set('status', filters.status);
    }

    if (filters.verificationStatus) {
      queryParameters = queryParameters.set('verificationStatus', filters.verificationStatus);
    }

    if (filters.leadSource) {
      queryParameters = queryParameters.set('leadSource', filters.leadSource);
    }

    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }
    return (await this.get('/v1/admin/leads', queryParameters)).data;
  }

  async getLeadById(id: number): Promise<Lead> {
    return (await this.get('/v1/admin/leads/' + id)).data;
  }

  async updateLeadAbilitation(id?: string, enabled?: boolean): Promise<Lead> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.append('enabled', enabled ?? '');
    return (
      await this.put('/v1/admin/leads/' + id + '/enabled', queryParameters)
    ).data;
  }

  async updatePartnerAbilitation(
    id?: string,
    enabled?: boolean
  ): Promise<Partner> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.append('enabled', enabled ?? '');
    return (
      await this.put('/v1/admin/partners/' + id + '/enabled', queryParameters)
    ).data;
  }

  async updatePartnerStatus(
    id?: string,
    status?: PartnerStatus,
    rejectionMotivation?: string
  ): Promise<Partner> {
    let action = '';
    switch (status) {
      case PartnerStatus.APPROVED:
        action = 'approve';
        break;
      case PartnerStatus.REJECTED:
        action = 'reject';
        break;
      case PartnerStatus.SUSPENDED:
        action = 'suspend';
        break;
      case PartnerStatus.UPDATE_REQUIRED:
        action = 'request-update';
        break;
      default:
        throw new Error('Invalid partner status');
    }
    return (
      await this.put('/v1/admin/partners/' + id + '/' + action, {
        motivation: rejectionMotivation,
      })
    ).data;
  }

  async getTopPartners(currentSort?: ColumnSorting[]): Promise<TopPartner[]> {
    let queryParameters = new HttpParams();

    if (currentSort) {
      queryParameters = this.sortingUtils.appendSortingParams(queryParameters, currentSort);
    }
    const response = await this.get('/v1/admin/dashboard/top-partners', queryParameters);
    return response.data;
  }

  async rejectPartner(partnerId: string, motivation: string): Promise<any> {
    return this.put(`/v1/admin/partners/${partnerId}/reject`,
      { motivation: motivation }
    );
  }

  async suspendPartner(partnerId: string, motivation: string): Promise<Partner> {
    return (await this.put(`/v1/admin/partners/${partnerId}/suspend`, { motivation: motivation })).data;
  }

  async requestPartnerUpdate(partnerId: string, motivation: string): Promise<Partner> {
    return (await this.put(`/v1/admin/partners/${partnerId}/request-update`, { motivation: motivation })).data;
  }


  async getPartners(
    page = 0,
    size: number = 25,
    status?: PartnerStatus,
    filter?: PartnerFilter,
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<Partner[]>> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.append('page', page).append('size', size);

    if (status != null) {
      queryParameters = queryParameters.append('status', status);
    }

    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }

    if (filter) {
      if (filter.country) {
        queryParameters = queryParameters.append('country', filter.country);
      }
      if (filter.region) {
        queryParameters = queryParameters.append('region', filter.region);
      }
      if (filter.district) {
        queryParameters = queryParameters.append('district', filter.district);
      }
      if (filter.withoutQrCode) {
        queryParameters = queryParameters.append('withoutQrCode', filter.withoutQrCode);
      }
      if (filter.q) {
        queryParameters = queryParameters.append('q', filter.q);
      }
      if (filter.languagesSpoken) {
        queryParameters = queryParameters.append('languagesSpoken', filter.languagesSpoken);
      }
    }

    return (await this.get('/v1/admin/partners', queryParameters)).data;
  }

  async getPartnerById(id?: string): Promise<Partner> {
    return (await this.get('/v1/admin/partners/' + id)).data;
  }

  async updateOfferRequestStatus(
    offerRequestId?: number,
    status?: OfferRequestStatusEnum
  ): Promise<QuoteRequest> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.append('status', status ?? '');
    return (
      await this.put('/v1/admin/offer-requests/' + offerRequestId + '/status', queryParameters)
    ).data;
  }

  async updateContactRequestStatus(
    contactRequestId?: number,
    status?: ContactStatus
  ): Promise<ContactRequest> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.append('status', status ?? '');
    return (
      await this.put('/v1/admin/contact-requests/' + contactRequestId + '/status', queryParameters)
    ).data;
  }

  async getContactRequests(
    page = 0,
    status: ContactStatus | undefined = undefined,
    partnerId?: number,
    filters?: ContactRequestFilter,
    size: number = 25,
    q?: string,
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<ContactRequest[]>> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters
      .append('page', page)
      .append('size', size);

    if (status) {
      queryParameters = queryParameters.append('status', status);
    }

    if (partnerId) {
      queryParameters = queryParameters.append('partnerId', partnerId);
    }

    if (filters) {
      if (filters.creationDateFrom) {
        queryParameters = queryParameters.append('creationDateFrom', filters.creationDateFrom
        );
      }
      if (filters.creationDateTo) {
        queryParameters = queryParameters.append('creationDateTo', filters.creationDateTo
        );
      }
    }

    if (q) {
      queryParameters = queryParameters.append('q', q)
    }

    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }

    return (await this.get('/v1/admin/contact-requests', queryParameters)).data;
  }

  async getContactRequest(leadId?: string): Promise<ContactRequest> {
    return (await this.get('/v1/admin/contact-requests/' + leadId)).data;
  }

  async getOfferRequests(
    page = 0,
    status?: OfferRequestStatusEnum,
    filters?: OfferRequestFilter,
    size?: number,
    q?: string,
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<QuoteRequest[]>> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters
      .append('page', page)
      .append('size', size ?? 25)

    if (status) {
      queryParameters = queryParameters.append('status', status);
    }

    if (filters) {
      if (filters.creationDateFrom) {
        queryParameters = queryParameters.append(
          'creationDateFrom',
          filters.creationDateFrom
        );
      }
      if (filters.creationDateTo) {
        queryParameters = queryParameters.append(
          'creationDateTo',
          filters.creationDateTo
        );
      }
      if (filters.status && Array.isArray(filters.status)) {
        queryParameters = queryParameters.append('status', filters.status.join(',')
        );
      }
      if (filters.treatmentType && Array.isArray(filters.treatmentType)) {
        queryParameters = queryParameters.append('treatmentType', filters.treatmentType.join(',')
        );
      }
      if (filters.dentalArch && Array.isArray(filters.dentalArch)) {
        queryParameters = queryParameters.append('dentalArch', filters.dentalArch.join(',')
        );
      }
      if (
        filters.requestMotivation &&
        Array.isArray(filters.requestMotivation)
      ) {
        queryParameters = queryParameters.append('requestMotivation', filters.requestMotivation.join(',')
        );
      }
    }

    if (q) {
      queryParameters = queryParameters.append('q', q);
    }

    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }

    const responsePage = (await this.get('/v1/admin/offer-requests', queryParameters)).data;
    if (responsePage?.content) {
      responsePage.content = responsePage.content.map((r: QuoteRequest) => this.applyQuoteRequestArchivedDefaults(r));
    }
    return responsePage;
  }

  async getOfferRequest(id: number): Promise<QuoteRequest> {
    const request = (await this.get('/v1/admin/offer-requests/' + id)).data;
    return this.applyQuoteRequestArchivedDefaults(request);
  }

  async getQuotes(
    page = 0,
    size = 25,
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<Quote[]>> {
    let queryParameters = new HttpParams()
      .append('page', page)
      .append('size', size)

    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }

    return (await this.get('/v1/admin/quotes', queryParameters)).data;
  }

  async getQuoteDetails(quoteId: number): Promise<Quote> {
    return (await this.get(`/v1/admin/quotes/${quoteId}`)).data;
  }

  async getCounters(): Promise<DashboardCounters> {
    return (await this.get('/v1/admin/dashboard/counters')).data;
  }

  async getKpiStats(): Promise<any> {
    return (await this.get('/v1/admin/dashboard/kpi')).data;
  }

  async getQrCodes(
    page = 0,
    size = 25,
    q = '',
    currentSort?: ColumnSorting[]
  ): Promise<GenericResponsePage<QrCode[]>> {
    let queryParameters = new HttpParams()
      .append('page', page)
      .append('size', size)

    if (q) {
      queryParameters = queryParameters.append('q', q);
    }

    if (currentSort) {
      queryParameters = this.sortingUtils.appendSortingParams(queryParameters, currentSort);
    }

    return (await this.get('/v1/qr-codes', queryParameters)).data;
  }

  async linkQrCodeToPartner(
    qrCodeId: number,
    partnerId: number
  ): Promise<QrCode> {
    const queryParameters = new HttpParams().set('partnerId', partnerId);
    const response = await this.put(`/v1/qr-codes/update/${qrCodeId}`,
      {},
      queryParameters
    );
    return response.data;
  }

  async getReports(
    page = 0,
    size = 10,
    currentSort?: ColumnSorting[],
    reportType?: string,
    q = '',
    status?: string,
  ): Promise<GenericResponsePage<Report[]>> {

    let queryParameters = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())

    if (q) {
      queryParameters = queryParameters.set('q', q);
    }

    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }

    if (reportType) {
      queryParameters = queryParameters.set('reportType', reportType)
    }

    if (status) {
      queryParameters = queryParameters.set('status', status)
    }

    return (await this.get('/v1/admin/reports', queryParameters)).data
  }

  /** Storico degli SMS in uscita, il più recente per primo. Solo ADMIN. */
  async getSmsHistory(page = 0, size = 10, query = ''): Promise<GenericResponsePage<SmsMessage[]>> {
    let queryParameters = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (query) {
      queryParameters = queryParameters.set('query', query);
    }

    return (await this.get('/v1/admin/sms', queryParameters)).data;
  }

  async getReportsById(reportId: number): Promise<Report> {
    return (await this.get(`/v1/admin/reports/${reportId}`)).data;
  }

  async updateReportStatus(reportId: number, status: string): Promise<Report> {
    return (await this.put(`/v1/admin/reports/${reportId}/status`, { status })).data;
  }

  async getInvoices(
    page = 0,
    size = 10,
    q: string = '',
    currentSort?: ColumnSorting[],
    filters?: InvoiceFilters
  ): Promise<GenericResponsePage<Invoice[]>> {
    let queryParameters = new HttpParams()
      .append('page', page)
      .append('size', size)
      .append('q', q);

    if (currentSort && currentSort.length !== 0) {
      queryParameters = queryParameters.append('orderBy', currentSort[0].attributeName ?? '');
      queryParameters = queryParameters.append('direction', currentSort[0].direction ?? '');
    }

    if (filters) {
      if (filters.issuedFrom) {
        queryParameters = queryParameters.append('issuedFrom', filters.issuedFrom);
      }
      if (filters.issuedTo) {
        queryParameters = queryParameters.append('issuedTo', filters.issuedTo);
      }
      if (filters.paidFrom) {
        queryParameters = queryParameters.append('paidFrom', filters.paidFrom);
      }
      if (filters.paidTo) {
        queryParameters = queryParameters.append('paidTo', filters.paidTo);
      }
      if (filters.minAmount !== undefined) {
        queryParameters = queryParameters.append('minAmount', filters.minAmount);
      }
      if (filters.maxAmount !== undefined) {
        queryParameters = queryParameters.append('maxAmount', filters.maxAmount);
      }
      if (filters.status) {
        queryParameters = queryParameters.append('status', filters.status);
      }
      if (filters.fiscalStatus) {
        queryParameters = queryParameters.append('fiscalStatus', filters.fiscalStatus);
      }
    }

    const response = await this.get('/v1/admin/billing/invoices', queryParameters);
    return response.data;
  }

  async getInvoiceById(invoiceId: number): Promise<Invoice> {
    const response = await this.get(`/v1/admin/billing/invoices/${invoiceId}`);
    return response.data;
  }

  /** Registra un pagamento ricevuto fuori piattaforma. paidOn: yyyy-MM-dd. */
  async markInvoicePaid(invoiceId: number, reference: string, paidOn: string, paymentMethod: InvoicePaymentMethod): Promise<Invoice> {
    const response = await this.post(`/v1/admin/billing/invoices/${invoiceId}/mark-paid`, { reference, paidOn, paymentMethod });
    return response.data;
  }

  /** Avvia/ritenta la fiscalizzazione fature.al di una fattura. */
  async fiscalizeInvoice(invoiceId: number): Promise<Invoice> {
    const response = await this.post(`/v1/admin/billing/invoices/${invoiceId}/fiscalize`, {});
    return response.data;
  }

  async downloadInvoicePdf(invoiceId: number): Promise<Blob> {
    return lastValueFrom(
      this.httpClient.get(
        `${this.getApiUrl()}/v1/admin/billing/invoices/${invoiceId}/pdf`,
        { responseType: 'blob' }
      )
    );
  }

  async getQuoteRequestsStats(): Promise<QuoteRequestStats> {
    return (await this.get('/v1/admin/dashboard/quote-requests/stats')).data;
  }

  async getQuoteRequestsTimeSeries(startDate?: string, endDate?: string): Promise<QuoteRequestTimeSeries[]> {
    let queryParameters = new HttpParams();
    if (startDate) queryParameters = queryParameters.set('startDate', startDate);
    if (endDate) queryParameters = queryParameters.set('endDate', endDate);
    return (await this.get('/v1/admin/dashboard/quote-requests/time-series', queryParameters)).data;
  }

  async getContactRequestsTimeSeries(startDate?: string, endDate?: string): Promise<ContactRequestTimeSeries[]> {
    let queryParameters = new HttpParams();
    if (startDate) queryParameters = queryParameters.set('startDate', startDate);
    if (endDate) queryParameters = queryParameters.set('endDate', endDate);
    return (await this.get('/v1/admin/dashboard/contact-requests/time-series', queryParameters)).data;
  }

  async getRevenueTimeSeries(startDate?: string, endDate?: string): Promise<RevenueTimeSeries[]> {
    let queryParameters = new HttpParams();
    if (startDate) queryParameters = queryParameters.set('startDate', startDate);
    if (endDate) queryParameters = queryParameters.set('endDate', endDate);
    return (await this.get('/v1/admin/dashboard/revenue/time-series', queryParameters)).data;
  }

  async getQuoteActivePartners(): Promise<PartnerQuotePerformance[]> {
    return (await this.get('/v1/admin/dashboard/quote-requests/active-partners')).data;
  }

  async getContactActivePartners(): Promise<PartnerContactPerformance[]> {
    return (await this.get('/v1/admin/dashboard/contact-requests/active-partners')).data;
  }

  private applyQuoteRequestArchivedDefaults(request: QuoteRequest): QuoteRequest {
    // Vedi partner.service: i placeholder vanno applicati SOLO alle richieste effettivamente
    // purgate dalla retention (segnalate da lead.purgedAt), non ai campi opzionali vuoti
    // delle richieste normali.
    if (!request.lead?.purgedAt) {
      return request;
    }
    return {
      ...request,
      panoramicPhoto1UrlLarge: request.panoramicPhoto1UrlLarge ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto2UrlLarge: request.panoramicPhoto2UrlLarge ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto1UrlMedium: request.panoramicPhoto1UrlMedium ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto2UrlMedium: request.panoramicPhoto2UrlMedium ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto1UrlThumb: request.panoramicPhoto1UrlThumb ?? 'assets/icons/archived-photo.svg',
      panoramicPhoto2UrlThumb: request.panoramicPhoto2UrlThumb ?? 'assets/icons/archived-photo.svg',
    };
  }

}
